import React, { useCallback, useEffect, useState } from 'react';
import { useLanguage } from '../../i18n/LanguageContext';
import { SharedItem, ShareKind, deleteShare, listSharedWithMe } from '../../api/sharingApi';
import { fill } from './fill';
import './sharing.css';

interface SharedWithMeProps {
  onOpenDocument: (documentId: number) => void;
  onOpenStoryboard: (storyboardId: number) => void;
  onOpenNote: (noteId: number) => void;
}

const KIND_ORDER: ShareKind[] = ['document', 'storyboard', 'note'];

// Everything other people have shared with this account. These items are
// deliberately absent from the user's own lists and search, so this page is
// the only way in to them.
const SharedWithMe: React.FC<SharedWithMeProps> = ({
  onOpenDocument, onOpenStoryboard, onOpenNote,
}) => {
  const { t } = useLanguage();
  const [items, setItems] = useState<SharedItem[] | null>(null);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      setItems(await listSharedWithMe());
    } catch {
      setItems([]);
      setError(t.sharing.loadFailed);
    }
  }, [t]);

  useEffect(() => { load(); }, [load]);

  const open = (item: SharedItem) => {
    if (item.kind === 'document') onOpenDocument(item.id);
    else if (item.kind === 'storyboard') onOpenStoryboard(item.id);
    else onOpenNote(item.id);
  };

  const leave = async (item: SharedItem) => {
    if (!window.confirm(fill(t.sharing.leaveConfirm, { name: item.name }))) return;
    try {
      await deleteShare(item.share_id);
      setItems((current) => (current || []).filter((row) => row.share_id !== item.share_id));
    } catch {
      setError(t.sharing.loadFailed);
    }
  };

  const label = (kind: ShareKind) => (
    kind === 'document' ? t.sharing.kindDocument
      : kind === 'storyboard' ? t.sharing.kindStoryboard
        : t.sharing.kindNote
  );

  return (
    <div className="shared-with-me">
      <h1>{t.sharing.sharedWithMeTitle}</h1>
      {error && <div className="share-error">{error}</div>}

      {items === null ? (
        <p className="share-empty">{t.common.loading}</p>
      ) : items.length === 0 ? (
        <p className="share-empty">{t.sharing.nothingShared}</p>
      ) : (
        KIND_ORDER.map((kind) => {
          const group = items.filter((item) => item.kind === kind);
          if (group.length === 0) return null;
          return (
            <section key={kind} className="shared-group">
              <h2>{label(kind)}</h2>
              <ul className="shared-list">
                {group.map((item) => (
                  <li key={item.share_id} className="shared-item">
                    <button type="button" className="shared-item-open" onClick={() => open(item)}>
                      <span className="shared-item-name">{item.name}</span>
                      <span className="shared-item-meta">
                        {fill(t.sharing.sharedBy, { name: item.owner.name })}
                        {' · '}
                        {item.role === 'editor' ? t.sharing.youCanEdit : t.sharing.youCanView}
                        {' · '}
                        {fill(t.sharing.updated, {
                          date: new Date(item.updated_at).toLocaleDateString(),
                        })}
                      </span>
                    </button>
                    <button type="button" className="shared-item-leave" onClick={() => leave(item)}>
                      {t.sharing.leave}
                    </button>
                  </li>
                ))}
              </ul>
            </section>
          );
        })
      )}
    </div>
  );
};

export default SharedWithMe;
